/**
 * Prepares a value for numeric parsing by removing unnecessary characters.
 * Handles formatted numbers (e.g., "1,234") and percentages (e.g., "56.78%").
 *
 * @param {*} value - The value to clean.
 * @returns {string} A cleaned string suitable for numeric parsing.
 */
export const cleanNumericString = (value) => {
    if (typeof value === 'string') {
        // Remove all characters except digits, decimal points, and minus signs
        return value.replace(/[^0-9.-]/g, '');
    }
    return value;
};

/**
 * Determines whether a value is numeric.
 *
 * @param {*} value - The value to check.
 * @returns {boolean} Returns whether the passed value is numeric (true) or not (false).
 */
export const isNumeric = (value) => {
    value = cleanNumericString(value);
    return !isNaN(parseFloat(value)) && isFinite(value);
};

/**
 * Parses a value into a numeric value.
 *
 * @param {*} value - The value to parse.
 * @returns {number} The parsed numeric value, or NaN if the value cannot be parsed.
 */
export const parseNumericValue = (value) => {
    value = cleanNumericString(value);
    return parseFloat(value);
};

/**
 * Compares two values for sorting purposes, with optional direction.
 * Handles both numeric and non-numeric comparisons.
 *
 * @param {string|number} a - The first value to compare. Can be a string or a number.
 * @param {string|number} b - The second value to compare. Can be a string or a number.
 * @param {string} direction - The direction of the sort ('asc' for ascending, 'desc' for descending).
 * @returns {number} Returns -1 if `a` should come before `b`, 1 if `b` should come before `a`, or 0 if they are equal.
 */
export const compareValues = (a, b, direction = 'asc') => {
    // Handle blank or null values explicitly
    const isBlank = (value) => value === null || value === undefined || value === '';

    if (isBlank(a) && isBlank(b)) return 0; // Both blank
    if (isBlank(a)) return 1; // Blank `a` comes last
    if (isBlank(b)) return -1; // Blank `b` comes last

    // Parse numeric values if both are numeric
    if (isNumeric(a) && isNumeric(b)) {
        a = parseNumericValue(a);
        b = parseNumericValue(b);
    }

    // Perform the comparison
    if (a < b) return direction === 'asc' ? -1 : 1;
    if (a > b) return direction === 'asc' ? 1 : -1;
    return 0;
};

/**
 * Formats a number with US English separators (commas) for thousands.
 *
 * @param {*} number - The number to format.
 * @returns The number as a formatted string.
 */
export const numberWithDelimiter = (number, locale = 'en-US') => {
    return new Intl.NumberFormat(locale).format(number);
};
